import React, { useState, useEffect, useContext } from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { PageHeader, Card, CardContent, Badge, Skeleton } from '../components/ui';

interface UserProfile {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export default function Profile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;

  useEffect(() => {
    if (!keycloak) return;
    keycloak
      .loadUserProfile()
      .then((p: any) => setProfile(p))
      .catch((err: any) => console.error('Failed to load user profile:', err));
  }, [keycloak]);

  const isAdmin = keycloak?.tokenParsed?.realm_access?.roles?.includes('admin') || false;
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || profile?.username || '';
  const initials =
    (profile?.firstName?.[0] || '') + (profile?.lastName?.[0] || '') ||
    (profile?.username?.slice(0, 2) || '').toUpperCase();

  const rows: { label: string; value?: React.ReactNode; mono?: boolean }[] = [
    { label: 'User ID', value: profile?.id, mono: true },
    {
      label: 'Role',
      value: (
        <Badge variant="muted">{isAdmin ? 'Administrator' : 'User'}</Badge>
      ),
    },
    { label: 'Username', value: profile?.username },
    { label: 'Email address', value: profile?.email },
    { label: 'First name', value: profile?.firstName },
    { label: 'Last name', value: profile?.lastName },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<UserIcon />}
        title="Profile"
        description="Your personal details and account information."
      />

      <Card>
        <CardContent className="p-6">
          {!profile ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 pb-6 border-b border-border">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-lg font-semibold uppercase">
                  {initials || '?'}
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-foreground truncate">{fullName || '—'}</p>
                  <p className="text-sm text-muted-foreground truncate">{profile.email}</p>
                </div>
              </div>
              <dl className="divide-y divide-border">
                {rows.map((r) => (
                  <div key={r.label} className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{r.label}</dt>
                    <dd
                      className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${
                        r.mono ? 'font-mono break-all' : ''
                      }`}
                    >
                      {r.value || <span className="text-muted-foreground">—</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
